import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Persona } from '../types/persona';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import * as personaService from '../services/personaService';
import { FiUpload, FiDownload, FiCheck, FiX, FiInfo, FiArrowLeft, FiFilter, FiSearch, FiSettings } from 'react-icons/fi';
import { Spinner } from '../components/common/Spinner';
import branding from '../branding.json';
import { PersonaMarkdown } from '../components/personas/PersonaMarkdown';

// Reusable tooltip component for buttons with markdown support
const ButtonWithTooltip = ({ 
  onClick, 
  className, 
  tooltipText, 
  children, 
  disabled = false 
}: { 
  onClick: (e: React.MouseEvent) => void, 
  className: string, 
  tooltipText: string, 
  children: React.ReactNode,
  disabled?: boolean
}) => {
  const { darkMode } = useTheme();
  const isMarkdown = tooltipText.includes('#') || tooltipText.includes('*') || tooltipText.includes('-');
  
  return (
    <div className="group relative">
      <button
        onClick={onClick}
        className={className}
        disabled={disabled}
      >
        {children}
      </button>
      <div className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 rounded shadow-lg z-10 
        text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none
        ${darkMode ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-800'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}
        ${isMarkdown ? 'whitespace-normal max-w-md w-80' : 'whitespace-nowrap'}`}>
        {isMarkdown ? (
          <PersonaMarkdown content={tooltipText} size="small" />
        ) : (
          tooltipText
        )}
        {/* Arrow */}
        <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent 
          ${darkMode ? 'border-b-gray-900' : 'border-b-white'}`}></div>
      </div>
    </div>
  );
};

export const ManagePersonas: React.FC = () => {
  const { darkMode } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [selectedPersonas, setSelectedPersonas] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPersonas, setFilteredPersonas] = useState<Persona[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importOptions, setImportOptions] = useState({
    updateExisting: true,
    skipDuplicates: false,
    matchBy: 'id' as 'id' | 'name' | 'both'
  });
  const [showImportOptions, setShowImportOptions] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Get brand colors from branding.json
  const primaryColor = branding.theme.primaryColor;
  const secondaryColor = branding.theme.secondaryColor;
  const accentColor = branding.theme.accentColor;
  
  // Card styling from branding.json
  const cardBorderRadius = branding.theme.cards.persona.borderRadius === 'lg' ? 'rounded-lg' : 'rounded-md';
  const cardShadow = branding.theme.cards.persona.shadow === 'md' ? 'shadow-md' : 'shadow-lg';
  const cardHoverEffect = branding.theme.cards.persona.hoverEffect === 'elevate' ? 'hover:shadow-xl' : 'hover:scale-105';

  // Fetch all personas
  const { data: personas, isLoading, error, refetch } = useQuery(
    ['personas'],
    async () => {
      console.log("Fetching personas for ManagePersonas component");
      const result = await personaService.getPersonas();
      console.log("Personas data received:", result);
      
      // Log the first persona's description fields to debug
      if (result && result.length > 0) {
        const firstPersona = result[0];
        console.log("First persona description fields:", {
          description: firstPersona.description,
          shortDescription: firstPersona.shortDescription,
          fullDescription: firstPersona.fullDescription,
          data: firstPersona.data
        });
      }
      
      return result;
    }
  );

  // Filter and sort personas based on search query and sort settings
  useEffect(() => {
    if (!personas) return;
    
    // First filter by search query
    let filtered = personas;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = personas.filter(persona => 
        persona.name.toLowerCase().includes(query) || 
        persona.tags?.some(tag => tag.toLowerCase().includes(query)) ||
        persona.shortDescription?.toLowerCase().includes(query)
      );
    }
    
    // Then sort the filtered results
    const sorted = [...filtered].sort((a, b) => {
      let valueA, valueB;
      
      // Handle different field types
      switch (sortField) {
        case 'name':
          valueA = a.name.toLowerCase();
          valueB = b.name.toLowerCase();
          break;
        case 'description':
          valueA = (a.shortDescription || '').toLowerCase();
          valueB = (b.shortDescription || '').toLowerCase();
          break;
        case 'updated':
          valueA = new Date(a.updated_at || a.updatedAt || 0).getTime();
          valueB = new Date(b.updated_at || b.updatedAt || 0).getTime();
          break;
        default:
          valueA = a.name.toLowerCase();
          valueB = b.name.toLowerCase();
      }
      
      // Compare based on direction
      if (sortDirection === 'asc') {
        return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
      } else {
        return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
      }
    });
    
    setFilteredPersonas(sorted);
  }, [personas, searchQuery, sortField, sortDirection]);

  const handleSelectPersona = (personaId: string) => {
    setSelectedPersonas(prev => {
      if (prev.includes(personaId)) {
        return prev.filter(id => id !== personaId);
      } else {
        return [...prev, personaId];
      }
    });
  };

  const handleSelectAll = () => {
    if (filteredPersonas) {
      if (selectedPersonas.length === filteredPersonas.length) {
        setSelectedPersonas([]);
      } else {
        setSelectedPersonas(filteredPersonas.map(p => p.id));
      }
    }
  };
  
  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Helper to render sort indicator
  const renderSortIndicator = (field: string) => {
    if (sortField !== field) return null;
    
    return (
      <span className="ml-1">
        {sortDirection === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  const handleExportSelected = async () => {
    if (selectedPersonas.length === 0) {
      showToast(
        "No personas selected",
        "warning"
      );
      return;
    }

    try {
      setIsExporting(true);
      await personaService.exportPersonasAsJsonl(selectedPersonas);
      showToast(
        `Successfully exported ${selectedPersonas.length} personas`,
        "success"
      );
    } catch (error) {
      console.error('Export error:', error);
      showToast(
        "Failed to export personas",
        "error"
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsImporting(true);
      setImportResults(null);
      
      const results = await personaService.importPersonas(file, importOptions);
      
      setImportResults(results);
      showToast(
        results.message,
        "success"
      );
      
      // Refresh the personas list
      refetch();
    } catch (error) {
      console.error('Import error:', error);
      showToast(
        "Failed to import personas",
        "error"
      );
    } finally {
      setIsImporting(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const toggleImportOptions = () => {
    setShowImportOptions(!showImportOptions);
  };

  return (
    <div className="container mx-auto p-4">
      {/* Header with gradient background from branding */}
      <div className={`mb-8 p-6 ${cardBorderRadius} ${cardShadow} bg-gradient-to-r from-blue-500 to-purple-600 text-white`}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold">Persona I/O Manager</h1>
            <p className="mt-2 opacity-90">Import, export, and manage your AI personas library</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="relative">
              <ButtonWithTooltip
                onClick={toggleImportOptions}
                className={`px-4 py-2 ${cardBorderRadius} flex items-center bg-white bg-opacity-20 hover:bg-opacity-30 text-white transition-all ${cardHoverEffect}`}
                tooltipText={`
## Import Options

Configure how persona imports are handled:

- **Update Existing**: When enabled, existing personas with matching IDs will be updated
- **Skip Duplicates**: When enabled, personas that already exist will be skipped instead of updated
- **Match By**: Control how duplicates are identified:
  - ID only: Match only by unique identifier
  - Name only: Match by persona name
  - Both: Match by either ID or name

*Click to open the configuration panel*
                `}
              >
                <FiSettings className="mr-2" />
                Import Options
              </ButtonWithTooltip>
              
              {showImportOptions && (
                <div className={`absolute right-0 mt-2 p-4 ${cardBorderRadius} ${cardShadow} z-10 w-64 ${
                  darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                }`}>
                  <h4 className="font-medium mb-3">Import Settings</h4>
                  
                  <div className="mb-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={importOptions.updateExisting}
                        onChange={(e) => setImportOptions({...importOptions, updateExisting: e.target.checked})}
                        className="mr-2"
                      />
                      <span className="text-sm">Update existing personas</span>
                    </label>
                  </div>
                  
                  <div className="mb-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={importOptions.skipDuplicates}
                        onChange={(e) => setImportOptions({...importOptions, skipDuplicates: e.target.checked})}
                        className="mr-2"
                      />
                      <span className="text-sm">Skip duplicates</span>
                    </label>
                  </div>
                  
                  <div className="mb-2">
                    <label className="text-sm block mb-1">Match duplicates by:</label>
                    <select
                      value={importOptions.matchBy}
                      onChange={(e) => setImportOptions({
                        ...importOptions, 
                        matchBy: e.target.value as 'id' | 'name' | 'both'
                      })}
                      className={`w-full p-1.5 ${cardBorderRadius} text-sm ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      <option value="id">ID only</option>
                      <option value="name">Name only</option>
                      <option value="both">ID or Name</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
            
            <ButtonWithTooltip
              onClick={handleImportClick}
              className={`px-4 py-2 ${cardBorderRadius} flex items-center bg-white bg-opacity-20 hover:bg-opacity-30 text-white transition-all ${cardHoverEffect}`}
              disabled={isImporting}
              tooltipText={`
## Import Personas

Upload personas from JSON or JSONL files to your library.

### Supported Formats:
- **JSON**: Single persona or array of personas
- **JSONL**: Multiple personas, one per line

### Required Fields:
- \`name\`: Persona name
- \`prompt\`: System prompt for the persona

### Optional Fields:
- \`tags\`: Array of categorization tags
- \`capabilities\`: Object with numeric ratings
- \`patterns\`: Best practices this persona follows
- \`antipatterns\`: Practices this persona avoids

*Click to select a file for import*
              `}
            >
              {isImporting ? (
                <Spinner size="sm" className="mr-2" />
              ) : (
                <FiUpload className="mr-2" />
              )}
              Import Personas
            </ButtonWithTooltip>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".json,.jsonl"
              className="hidden"
            />
            
            <ButtonWithTooltip
              onClick={handleExportSelected}
              className={`px-4 py-2 ${cardBorderRadius} flex items-center bg-white bg-opacity-20 hover:bg-opacity-30 text-white transition-all ${cardHoverEffect} ${selectedPersonas.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={selectedPersonas.length === 0 || isExporting}
              tooltipText={`## Export Selected (${selectedPersonas.length})

Export your selected personas to a JSONL file for:
- **Backup**: Create a safe backup of your personas
- **Sharing**: Share personas with colleagues or other instances
- **Version Control**: Store personas in version control systems
- **Migration**: Move personas between environments

### Export Format:
- **JSONL**: Each persona is exported as a complete JSON object on a separate line
- All fields are preserved, including capabilities, patterns, and metadata

${selectedPersonas.length === 0 ? '*Select personas to export*' : '*Click to download the selected personas as a JSONL file*'}
                `}
            >
              {isExporting ? (
                <Spinner size="sm" className="mr-2" />
              ) : (
                <FiDownload className="mr-2" />
              )}
              Export Selected ({selectedPersonas.length})
            </ButtonWithTooltip>
            
            <button
              onClick={() => navigate('/personas')}
              className={`px-4 py-2 ${cardBorderRadius} flex items-center bg-white bg-opacity-20 hover:bg-opacity-30 text-white transition-all ${cardHoverEffect}`}
            >
              <FiArrowLeft className="mr-2" />
              Back to Personas List
            </button>
          </div>
        </div>
      </div>
      
      {/* Import Results */}
      {importResults && (
        <div className={`mb-6 p-4 ${cardBorderRadius} border ${cardShadow} ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <h2 className="text-lg font-semibold mb-2 flex items-center">
            <FiInfo className="mr-2" style={{ color: primaryColor }} />
            Import Results
          </h2>
          <p className="mb-4">{importResults.message}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Imported */}
            {importResults.results.imported.length > 0 && (
              <div className={`p-3 ${cardBorderRadius} ${
                darkMode ? 'bg-green-900 bg-opacity-20 border border-green-800' : 'bg-green-50 border border-green-200'
              }`}>
                <h3 className={`text-sm font-medium mb-2 flex items-center ${
                  darkMode ? 'text-green-300' : 'text-green-700'
                }`}>
                  <FiCheck className="mr-1" />
                  Imported ({importResults.results.imported.length})
                </h3>
                <ul className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {importResults.results.imported.map((item: any, index: number) => (
                    <li key={`imported-${index}`} className="mb-1">
                      {item.name} ({item.id})
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Updated */}
            {importResults.results.updated.length > 0 && (
              <div className={`p-3 ${cardBorderRadius} ${
                darkMode ? 'bg-blue-900 bg-opacity-20 border border-blue-800' : 'bg-blue-50 border border-blue-200'
              }`}>
                <h3 className={`text-sm font-medium mb-2 flex items-center ${
                  darkMode ? 'text-blue-300' : 'text-blue-700'
                }`}>
                  <FiCheck className="mr-1" />
                  Updated ({importResults.results.updated.length})
                </h3>
                <ul className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {importResults.results.updated.map((item: any, index: number) => (
                    <li key={`updated-${index}`} className="mb-1">
                      {item.name} ({item.id})
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Skipped */}
            {importResults.results.skipped && importResults.results.skipped.length > 0 && (
              <div className={`p-3 ${cardBorderRadius} ${
                darkMode ? 'bg-yellow-900 bg-opacity-20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <h3 className={`text-sm font-medium mb-2 flex items-center ${
                  darkMode ? 'text-yellow-300' : 'text-yellow-700'
                }`}>
                  <FiInfo className="mr-1" />
                  Skipped ({importResults.results.skipped.length})
                </h3>
                <ul className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {importResults.results.skipped.map((item: any, index: number) => (
                    <li key={`skipped-${index}`} className="mb-1">
                      {item.name} ({item.id})
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Failed */}
            {importResults.results.failed.length > 0 && (
              <div className={`p-3 ${cardBorderRadius} ${
                darkMode ? 'bg-red-900 bg-opacity-20 border border-red-800' : 'bg-red-50 border border-red-200'
              }`}>
                <h3 className={`text-sm font-medium mb-2 flex items-center ${
                  darkMode ? 'text-red-300' : 'text-red-700'
                }`}>
                  <FiX className="mr-1" />
                  Failed ({importResults.results.failed.length})
                </h3>
                <ul className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {importResults.results.failed.map((item: any, index: number) => (
                    <li key={`failed-${index}`} className="mb-1">
                      {item.name}: {item.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Search and Selection Controls */}
      <div className={`mb-4 p-4 ${cardBorderRadius} ${cardShadow} ${
        darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
      }`}>
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          {/* Search input */}
          <div className="relative w-full md:w-1/3">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
            </div>
            <input
              type="text"
              placeholder="Search personas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-10 pr-4 py-2 w-full ${cardBorderRadius} border ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
          </div>
          
          <div className="flex items-center justify-between w-full md:w-auto">
            <div className="flex items-center">
              <ButtonWithTooltip
                onClick={handleSelectAll}
                className={`px-3 py-1 ${cardBorderRadius} ${
                  darkMode 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                } transition-colors`}
                tooltipText={filteredPersonas && selectedPersonas.length === filteredPersonas.length 
                  ? 'Deselect all personas' 
                  : 'Select all personas'}
              >
                {filteredPersonas && selectedPersonas.length === filteredPersonas.length 
                  ? 'Deselect All' 
                  : 'Select All'}
              </ButtonWithTooltip>
              <span className="ml-4">
                {selectedPersonas.length} of {filteredPersonas?.length || 0} selected
              </span>
              
              {/* View toggle buttons */}
              <div className="ml-4 flex border rounded-md overflow-hidden">
                <ButtonWithTooltip
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1 ${
                    viewMode === 'grid'
                      ? darkMode 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-blue-500 text-white'
                      : darkMode 
                        ? 'bg-gray-700 text-gray-300' 
                        : 'bg-gray-100 text-gray-700'
                  }`}
                  tooltipText="View personas in a grid layout"
                >
                  Grid
                </ButtonWithTooltip>
                <ButtonWithTooltip
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1 ${
                    viewMode === 'list'
                      ? darkMode 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-blue-500 text-white'
                      : darkMode 
                        ? 'bg-gray-700 text-gray-300' 
                        : 'bg-gray-100 text-gray-700'
                  }`}
                  tooltipText="View personas in a list layout"
                >
                  List
                </ButtonWithTooltip>
              </div>
            </div>
            
            {selectedPersonas.length > 0 && (
              <ButtonWithTooltip
                onClick={() => setSelectedPersonas([])}
                className={`text-sm ${
                  darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'
                } ml-4`}
                tooltipText="Clear all selected personas"
              >
                Clear Selection
              </ButtonWithTooltip>
            )}
          </div>
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center p-8">
          <Spinner size="lg" color={primaryColor} />
        </div>
      ) : error ? (
        <div className={`text-center py-10 ${cardBorderRadius} ${cardShadow} ${
          darkMode ? 'bg-red-900 bg-opacity-20 text-red-300' : 'bg-red-50 text-red-600'
        }`}>
          Error loading personas
        </div>
      ) : viewMode === 'grid' ? (
        // Grid View
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPersonas?.map((persona: Persona) => (
            <div 
              key={persona.id}
              className={`persona-card relative ${
                darkMode 
                  ? 'bg-gray-800 text-white' 
                  : 'bg-white text-gray-900'
              } ${cardBorderRadius} ${cardShadow} p-4 transition-all ${
                selectedPersonas.includes(persona.id) 
                  ? 'border-2 border-blue-500' 
                  : darkMode 
                    ? 'border border-gray-700 hover:border-blue-400' 
                    : 'border border-gray-200 hover:border-blue-300'
              } cursor-pointer`}
              onClick={() => handleSelectPersona(persona.id)}
            >
              {/* Checkbox at top left */}
              <div className="absolute top-3 left-3">
                <div className={`w-5 h-5 ${cardBorderRadius} flex items-center justify-center ${
                  selectedPersonas.includes(persona.id)
                    ? `bg-blue-500 text-white`
                    : darkMode ? 'border border-gray-600 bg-gray-700' : 'border border-gray-300 bg-white'
                }`}>
                  {selectedPersonas.includes(persona.id) && (
                    <FiCheck size={14} />
                  )}
                </div>
              </div>
              
              {/* Title moved to top, closer to checkbox */}
              <h3 className="text-lg font-semibold bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-600 mb-3 pl-8">
                {persona.name}
              </h3>
              
              <div className="flex items-center mb-3">
                <div className="relative ml-8">
                  {/* Simple avatar circle with first letter */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white
                    bg-gradient-to-r from-blue-500 to-purple-600`}>
                    {persona.name.charAt(0).toUpperCase()}
                  </div>
                </div>
                
                <div className="ml-3">
                  <div className="flex flex-wrap gap-1">
                    {persona.tags.slice(0, 3).map((tag, index) => (
                      <span 
                        key={`${persona.id}-tag-${index}`} 
                        className={`px-2 py-0.5 ${
                          darkMode 
                            ? 'bg-gray-700 text-gray-300' 
                            : 'bg-gray-100 text-gray-700'
                        } text-xs rounded-full`}
                      >
                        {tag}
                      </span>
                    ))}
                    {persona.tags.length > 3 && (
                      <div className="group relative">
                        <span className={`px-2 py-0.5 ${
                          darkMode 
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        } text-xs rounded-full cursor-pointer`}>
                          +{persona.tags.length - 3}
                        </span>
                        
                        {/* Tooltip showing remaining tags */}
                        <div className={`absolute bottom-full left-0 mb-2 w-64 p-2 rounded shadow-lg z-10 transform scale-0 group-hover:scale-100 transition-transform origin-bottom-left ${
                          darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'
                        }`}>
                          <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>All tags:</div>
                          <div className="flex flex-wrap gap-1">
                            {persona.tags.slice(3).map((tag, i) => (
                              <span 
                                key={i} 
                                className={`px-2 py-0.5 ${
                                  darkMode 
                                    ? 'bg-gray-700 text-gray-300' 
                                    : 'bg-gray-100 text-gray-700'
                                } text-xs rounded-full`}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <p className={`text-sm line-clamp-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {(() => {
                    console.log(`Rendering description for ${persona.name}:`, {
                      description: persona.description,
                      shortDescription: persona.shortDescription,
                      fullDescription: persona.fullDescription,
                      prompt: persona.prompt,
                      data: persona.data
                    });
                  
                    // Try to get description from various possible locations
                    const description = 
                      persona.description || 
                      persona.shortDescription || 
                      persona.fullDescription || 
                      (persona.data && persona.data.description) ||
                      (persona.data && persona.data.shortDescription) ||
                      persona.prompt || // Use prompt as fallback
                      'No description available';
                  
                    console.log(`Final description for ${persona.name}:`, description);
                    return description;
                  })()}
                </p>
              </div>
              
              {/* Capabilities preview */}
              {persona.capabilities && Object.keys(persona.capabilities).length > 0 && (
                <div className="mb-4 space-y-2">
                  {Object.entries(persona.capabilities)
                    .slice(0, 2)
                    .map(([key, value]) => (
                      <div key={key} className="flex items-center">
                        <span className={`text-xs capitalize w-24 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {key}:
                        </span>
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full dark:bg-gray-700">
                          <div 
                            className="h-1.5 rounded-full bg-blue-500" 
                            style={{ width: `${(value as number) * 10}%` }}
                          ></div>
                        </div>
                        <span className="ml-2 text-xs">{value}</span>
                      </div>
                    ))}
                  {Object.keys(persona.capabilities).length > 2 && (
                    <div className="text-xs text-center">
                      <span className={`${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                        +{Object.keys(persona.capabilities).length - 2} more
                      </span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="text-xs text-gray-500">
                Updated: {new Date(persona.updated_at || persona.updatedAt || "").toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // List View
        <div className={`${cardBorderRadius} ${cardShadow} border ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={filteredPersonas.length > 0 && selectedPersonas.length === filteredPersonas.length}
                      onChange={handleSelectAll}
                    />
                    <button 
                      className="flex items-center focus:outline-none"
                      onClick={() => handleSort('name')}
                    >
                      <span>Name</span>
                      {renderSortIndicator('name')}
                    </button>
                  </div>
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  <button 
                    className="flex items-center focus:outline-none"
                    onClick={() => handleSort('description')}
                  >
                    <span>Description</span>
                    {renderSortIndicator('description')}
                  </button>
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Tags
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  <button 
                    className="flex items-center focus:outline-none"
                    onClick={() => handleSort('updated')}
                  >
                    <span>Updated</span>
                    {renderSortIndicator('updated')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {filteredPersonas?.map((persona: Persona) => (
                <tr 
                  key={persona.id}
                  className={`${
                    selectedPersonas.includes(persona.id)
                      ? darkMode ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50'
                      : ''
                  } cursor-pointer hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                  onClick={() => handleSelectPersona(persona.id)}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`w-5 h-5 ${cardBorderRadius} mr-3 flex items-center justify-center ${
                        selectedPersonas.includes(persona.id)
                          ? `bg-blue-500 text-white`
                          : darkMode ? 'border border-gray-600' : 'border border-gray-300'
                      }`}>
                        {selectedPersonas.includes(persona.id) && (
                          <FiCheck size={14} />
                        )}
                      </div>
                      <span className="font-medium">{persona.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm line-clamp-1">
                      {(() => {
                        // Use the same approach as in the grid view
                        const description = 
                          persona.description || 
                          persona.shortDescription || 
                          persona.fullDescription || 
                          (persona.data && persona.data.description) ||
                          (persona.data && persona.data.shortDescription) ||
                          persona.prompt || // Use prompt as fallback
                          'No description available';
                      
                        return description;
                      })()}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {persona.tags.slice(0, 3).map((tag, index) => (
                        <span 
                          key={`${persona.id}-tag-${index}`} 
                          className={`px-2 py-0.5 ${
                            darkMode 
                              ? 'bg-gray-700 text-gray-300' 
                              : 'bg-gray-100 text-gray-700'
                          } text-xs rounded-full`}
                        >
                          {tag}
                        </span>
                      ))}
                      {persona.tags.length > 3 && (
                        <span className={`px-2 py-0.5 ${
                          darkMode 
                            ? 'bg-gray-700 text-gray-300' 
                            : 'bg-gray-100 text-gray-700'
                        } text-xs rounded-full`}>
                          +{persona.tags.length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {new Date(persona.updated_at || persona.updatedAt || "").toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Empty state with import CTA */}
      {filteredPersonas?.length === 0 && !isLoading && (
        <div className={`text-center py-16 ${cardBorderRadius} ${cardShadow} ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
        } border`}>
          <div className="flex flex-col items-center">
            <FiFilter className="h-12 w-12 mb-4 opacity-30" />
            <h3 className="text-xl font-medium mb-2">No personas found</h3>
            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-4`}>
              {searchQuery ? 'Try adjusting your search query' : 'Import personas to get started'}
            </p>
            <div className="flex gap-3">
              {searchQuery && (
                <ButtonWithTooltip
                  onClick={() => setSearchQuery('')}
                  className={`px-4 py-2 ${cardBorderRadius} ${
                    darkMode 
                      ? `bg-blue-600 hover:bg-blue-700` 
                      : `bg-blue-500 hover:bg-blue-600`
                  } text-white transition-colors`}
                  tooltipText="Clear the current search query"
                >
                  Clear Search
                </ButtonWithTooltip>
              )}
              {!searchQuery && (
                <ButtonWithTooltip
                  onClick={handleImportClick}
                  className={`px-4 py-2 ${cardBorderRadius} flex items-center ${
                    darkMode 
                      ? `bg-green-600 hover:bg-green-700` 
                      : `bg-green-500 hover:bg-green-600`
                  } text-white transition-colors`}
                  tooltipText="Import personas from JSON or JSONL files"
                >
                  <FiUpload className="mr-2" />
                  Import Personas
                </ButtonWithTooltip>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

